import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Star, Phone } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import api from "../../lib/api";
import { Button } from "../ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_HERO_IMAGE = `${BACKEND_URL}/api/uploads/photos/interior-hero-1135be40.jpg`;
const PHONE_NUMBER = "0618885582";
const PHONE_DISPLAY = "06 18885582";

export const Hero = () => {
  const { t, lang } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [heroPhoto, setHeroPhoto] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/photos", { params: { section: "hero" } }).then(({ data }) => setHeroPhoto(data[0] || null)).catch(() => setHeroPhoto(null));
  }, []);

  const heroImageUrl = heroPhoto ? `${BACKEND_URL}${heroPhoto.url}` : FALLBACK_HERO_IMAGE;
  const heroImageAlt = heroPhoto ? ((lang === "nl" ? heroPhoto.alt_text_nl : heroPhoto.alt_text_en) || "EliteTheBarber") : "Interieur van EliteTheBarber barbershop in Utrecht";

  return (
    <section data-testid="hero-section" className="relative min-h-[85vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={heroImageUrl} alt={heroImageAlt} className="w-full h-full object-cover object-top sm:object-[center_46%]" data-testid="hero-background-image" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-24 w-full">
        <div className="max-w-2xl animate-fade-up">
          <p className="text-gold text-xs sm:text-sm uppercase tracking-[0.3em] mb-4">Barbershop &middot; Utrecht</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white mb-6">{t("hero.title")}</h1>
          <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-8 max-w-lg">{t("hero.subtitle")}</p>

          <div className="flex flex-wrap items-center gap-4 mb-10">
            <Link to="/boeken">
              <Button data-testid="hero-book-button" className="bg-gold text-white hover:bg-gold-hover rounded-none px-8 py-6 text-base transition-colors">
                {t("hero.cta")}
              </Button>
            </Link>
            <a href={`tel:${PHONE_NUMBER}`} data-testid="hero-phone-button">
              <Button variant="outline" className="bg-transparent border border-white/20 text-white hover:border-gold hover:text-gold rounded-none px-8 py-6 text-base transition-colors gap-2">
                <Phone size={18} />
                {PHONE_DISPLAY}
              </Button>
            </a>
            <Link to="/diensten">
              <Button data-testid="hero-services-button" variant="outline" className="bg-transparent border border-white/20 text-white hover:border-gold hover:text-gold rounded-none px-8 py-6 text-base transition-colors">
                {t("hero.ctaSecondary")}
              </Button>
            </Link>
          </div>

          {settings && (
            <div data-testid="hero-rating-badge" className="flex items-center gap-3 text-white/90">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-gold text-gold" />
                ))}
              </div>
              <span className="text-sm">
                {settings.review_score.toFixed(1).replace(".", ",")} / 5 — {t("hero.ratingText", { count: settings.review_count })}
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
